import { SOURCES } from "@repo/ping-react-js";

export enum PingDataPlan {
  FREE = "FREE",
  PLAN_STANDARD = "PLAN_STANDARD",
  PLAN_PRO = "PLAN_PRO",
}

export type FreePlanVisibleAoaField = {
  sectionId: string;
  source?: string;
  sourceField?: string;
  label?: string;
};

export const FREE_PLAN_VISIBLE_AOA_FIELDS: FreePlanVisibleAoaField[] = [
  {
    sectionId: SOURCES.PING_HAZARD,
    source: SOURCES.DISTANCE_TO_COAST,
    sourceField: "distance_to_coast_feet",
  },
];

/** A plan's billed facts, as served by /api/v1/pli/plans from the backend plan table. */
export type PingDataPlanInfo = {
  plan: PingDataPlan;
  price_cents: number;
  included_lookups: number;
};

/** A plan card's copy, which stays on the front end; prices and lookups come from PingDataPlanInfo. */
export type PingDataPlanDisplay = {
  displayName: string;
  highlightedSources: string[];
  /** Sources beyond highlightedSources, shown as a "+N Sources" link to sourcesUrl. */
  moreSources?: { count: number; url: string };
  /** Visually highlighted as the recommended plan. */
  featured?: boolean;
};

const ENTRY_HIGHLIGHTED_SOURCES = ["Distance To Coast"];
const PAID_HIGHLIGHTED_SOURCES = [
  ...ENTRY_HIGHLIGHTED_SOURCES,
  "Storm Surge HU Category",
  "EQ Zone",
  "Total Assessed Value",
  "Year Built",
  "Roof Type",
];
// PH2's published docs page (same as the PH2 datasource's vendor_api_docs_url in dataweb).
const PING_HAZARD_MORE_SOURCES = { count: 22, url: "https://docs.pingintel.com/ping-hazard-v2" };

export const PING_DATA_PLAN_DISPLAY: Record<PingDataPlan, PingDataPlanDisplay> = {
  [PingDataPlan.FREE]: {
    displayName: "Entry",
    highlightedSources: ENTRY_HIGHLIGHTED_SOURCES,
  },
  [PingDataPlan.PLAN_STANDARD]: {
    displayName: "Standard",
    highlightedSources: PAID_HIGHLIGHTED_SOURCES,
    moreSources: PING_HAZARD_MORE_SOURCES,
  },
  [PingDataPlan.PLAN_PRO]: {
    displayName: "Pro",
    highlightedSources: PAID_HIGHLIGHTED_SOURCES,
    moreSources: PING_HAZARD_MORE_SOURCES,
    featured: true,
  },
};

export const getPingDataPlanDisplay = (plan: PingDataPlan): PingDataPlanDisplay => {
  const display = PING_DATA_PLAN_DISPLAY[plan];
  if (!display) {
    throw new Error(`No card copy for Ping Data plan ${plan} in PING_DATA_PLAN_DISPLAY`);
  }
  return display;
};

/** Throws unless /api/v1/pli/plans and PING_DATA_PLAN_DISPLAY list exactly the same plans. */
export const assertPlansMatchDisplay = (plans: PingDataPlanInfo[]) => {
  const served = plans.map((p) => p.plan as string);
  const displayed = Object.keys(PING_DATA_PLAN_DISPLAY);
  const onlyServed = served.filter((plan) => !displayed.includes(plan));
  const onlyDisplayed = displayed.filter((plan) => !served.includes(plan));
  if (onlyServed.length || onlyDisplayed.length) {
    throw new Error(
      `/api/v1/pli/plans and PING_DATA_PLAN_DISPLAY disagree: only served [${onlyServed.join(", ")}], ` +
        `only in PING_DATA_PLAN_DISPLAY [${onlyDisplayed.join(", ")}]`,
    );
  }
};

export const findPingDataPlan = (plans: PingDataPlanInfo[], plan: PingDataPlan): PingDataPlanInfo => {
  const info = plans.find((p) => p.plan === plan);
  if (!info) {
    throw new Error(`Plan ${plan} missing from /api/v1/pli/plans (got ${plans.map((p) => p.plan).join(", ")})`);
  }
  return info;
};

export const isPingDataPlan = (value: unknown): value is PingDataPlan => {
  return Object.values(PingDataPlan).includes(value as PingDataPlan);
};
